import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock, Ruler, Check, Zap, ShieldCheck, Phone } from "lucide-react";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";

const PROJECTS = [
  {
    key: "shoreditch-strip-out",
    num: "01",
    type: "Commercial Demolition · Stage 01",
    title: "Shoreditch Office Strip-Out",
    location: "Shoreditch · E1",
    duration: "9 working days",
    size: "2,400 sq ft",
    intro:
      "A commercial floor handed back by an outgoing tenant — full soft strip required before Cat-A fit-out.",
    did: [
      "Full soft strip of partitions, ceilings & floor finishes",
      "Kitchen, bathrooms and signage removed",
      "Licensed waste carried away under chain of custody",
      "Handed over broom-clean and ready for fit-out",
    ],
    result:
      "2,400 sq ft commercial floor stripped and cleared in 9 working days with zero snags on handover.",
    before:
      "https://images.pexels.com/photos/5974377/pexels-photo-5974377.jpeg?auto=compress&cs=tinysrgb&w=1600",
    after:
      "https://static.prod-images.emergentagent.com/jobs/89f2f8d9-74f4-4335-b690-b9a815d801c5/images/29a394c6ebac1293f3df48f8711b0e12b2dbb9b14c818612a289626a5e4e2fbe.png",
  },
  {
    key: "dagenham-kitchen",
    num: "02",
    type: "Residential Refurbishment",
    title: "Dagenham Family Kitchen",
    location: "Dagenham · RM10",
    duration: "3 weeks",
    size: "Kitchen + dining",
    intro:
      "A tired kitchen and dining space rebuilt from the walls out — brighter, calmer, and built for family life.",
    did: [
      "Stripped existing kitchen and lifted flooring",
      "Re-plastered walls and ceilings",
      "Fitted new units, worktops & splashback tiling",
      "Full redecoration, new flooring and snagging",
    ],
    result:
      "Three-week programme delivered on budget. Family moved back into a brighter, modern kitchen with better flow.",
    before:
      "https://images.pexels.com/photos/6585598/pexels-photo-6585598.jpeg?auto=compress&cs=tinysrgb&w=1600",
    after:
      "https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
  {
    key: "canary-wharf-office",
    num: "03",
    type: "Commercial Refurbishment · Fit-Out",
    title: "Canary Wharf Retail Unit",
    location: "Canary Wharf · E14",
    duration: "4 weeks · Out-of-hours",
    size: "Ground-floor retail",
    intro:
      "A live retail unit needing a full interior refresh — delivered around trading hours with zero disruption.",
    did: [
      "Out-of-hours partition and ceiling works",
      "New LVT flooring throughout",
      "Full decoration, lighting upgrade & fit-out",
      "Snagging, handover and relaunch",
    ],
    result:
      "Four-week programme delivered with zero trading disruption. Store relaunched on schedule.",
    before:
      "https://images.pexels.com/photos/380769/pexels-photo-380769.jpeg?auto=compress&cs=tinysrgb&w=1600",
    after:
      "https://images.pexels.com/photos/1170412/pexels-photo-1170412.jpeg?auto=compress&cs=tinysrgb&w=1600",
  },
];

const TRUST_BADGES = [
  { icon: Zap, label: "Fast", sub: "Same-day response" },
  { icon: ShieldCheck, label: "Reliable", sub: "Fully insured" },
  { icon: Phone, label: "Easy", sub: "Call or WhatsApp" },
];

function Chip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 border border-[#E5DDD0] bg-[#FAF8F4] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-[#4D4037]">
      {Icon && <Icon className="w-3 h-3 text-[#B8732C]" />}
      {children}
    </span>
  );
}

export default function RecentProjects() {
  return (
    <section
      id="projects"
      data-testid="recent-projects-section"
      className="bg-[#FAF8F4] text-[#2B2420] py-20 md:py-32 border-y border-[#E5DDD0]"
    >
      <div className="px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 mb-16 md:mb-24">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-5 font-bold">
              <span className="brand-gold-divider" /> Recent Projects
            </div>
            <h2
              className="font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[96px]"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Real Work. <br /> <span className="text-[#B8732C]">Real Results.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col justify-end">
            <p className="text-[#4D4037] text-base md:text-lg leading-relaxed">
              Three recent London projects — drag each slider to reveal the
              before &amp; after. Every job delivered on programme, on budget,
              and handed over clean.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {TRUST_BADGES.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={b.label}
                    data-testid={`real-work-badge-${i}`}
                    className="border border-[#E5DDD0] bg-white p-4 text-center"
                  >
                    <Icon className="w-5 h-5 text-[#B8732C] mx-auto" strokeWidth={1.75} />
                    <div
                      className="mt-2 font-black text-base md:text-lg"
                      style={{ fontFamily: "Cabinet Grotesk" }}
                    >
                      {b.label}
                    </div>
                    <div className="text-[10px] md:text-[11px] text-[#8A7F74] mt-0.5 leading-tight">
                      {b.sub}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Project rows */}
        <div className="space-y-20 md:space-y-28">
          {PROJECTS.map((p, i) => {
            const reverse = i % 2 === 1;
            return (
              <motion.article
                key={p.key}
                data-testid={`project-card-${p.key}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.65 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center"
              >
                <div
                  className={`md:col-span-7 ${
                    reverse ? "md:order-2" : "md:order-1"
                  }`}
                >
                  <div className="border border-[#E5DDD0] bg-white p-2 md:p-3 shadow-[0_20px_60px_-32px_rgba(43,36,32,0.28)] lift-hover">
                    <BeforeAfterSlider
                      before={p.before}
                      after={p.after}
                      alt={p.title}
                      testid={`ba-slider-${p.key}`}
                      aspect="aspect-[16/10]"
                    />
                  </div>
                </div>

                <div
                  className={`md:col-span-5 ${
                    reverse ? "md:order-1" : "md:order-2"
                  }`}
                >
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#8A7F74] font-bold mb-4">
                    <span
                      className="font-black text-[#B8732C] text-4xl md:text-5xl leading-none"
                      style={{ fontFamily: "Cabinet Grotesk" }}
                    >
                      {p.num}
                    </span>
                    <span className="flex-1 h-px bg-[#E5DDD0]" />
                    <span>{p.type}</span>
                  </div>

                  <h3
                    className="font-black tracking-tighter leading-[1] text-3xl md:text-4xl lg:text-5xl"
                    style={{ fontFamily: "Cabinet Grotesk" }}
                  >
                    {p.title}
                  </h3>

                  <div className="mt-5 flex flex-wrap gap-2">
                    <Chip icon={MapPin}>{p.location}</Chip>
                    <Chip icon={Clock}>{p.duration}</Chip>
                    <Chip icon={Ruler}>{p.size}</Chip>
                  </div>

                  <p className="mt-6 text-[#4D4037] leading-relaxed">{p.intro}</p>

                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#8A7F74] font-bold mb-3">
                      What we did
                    </div>
                    <ul className="space-y-2.5">
                      {p.did.map((d) => (
                        <li
                          key={d}
                          className="flex items-start gap-3 text-sm md:text-[15px] text-[#2B2420]"
                        >
                          <span className="mt-[5px] w-4 h-4 shrink-0 bg-[#B8732C] text-white flex items-center justify-center">
                            <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                          </span>
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-7 border-l-2 border-[#C9A24C] pl-5 bg-[#F5EFE5] py-4 pr-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#B8732C] font-bold mb-1">
                      Result
                    </div>
                    <p className="text-[#2B2420] font-semibold leading-snug">{p.result}</p>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Final CTA */}
        <div className="mt-20 md:mt-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-[#E5DDD0] pt-12">
          <div>
            <h3
              className="font-black tracking-tight text-2xl md:text-3xl"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Your project could be next.
            </h3>
            <p className="mt-2 text-[#8A7F74]">
              Free quotes · Same-day response · Small jobs welcome.
            </p>
          </div>
          <a
            href="#quote"
            data-testid="recent-projects-cta"
            className="inline-flex items-center gap-2 bg-[#B8732C] hover:bg-[#9A5D1E] text-white px-7 py-5 text-sm font-bold uppercase tracking-[0.15em] transition-all lift-hover"
          >
            Start your project <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
